import { useRef, useState } from 'react';
import { Step1Dates } from './booking-steps/step-dates';
import { Step2Vehicles } from './booking-steps/step-vehicles';
import { Step3Transfer } from './booking-steps/step-transfer';
import { Step4Quotation } from './booking-steps/step-quotation';
import { Step5Personal } from './booking-steps/step-personal-info';
import { createBooking, createBookingIdempotencyKey, requestBookingQuote } from './booking-api';
import { initialState, validateStep } from './booking-form.utils';
import { clearBlockedTimes } from './booking-availability';

const quoteInputFields = new Set([
    'checkin_date',
    'checkin_time',
    'checkout_date',
    'checkout_time',
    'vehicles',
    'has_transfer',
    'transfer_type',
    'passengers_outbound',
    'dropoff_place',
    'dropoff_place_detail',
    'embark_detail',
    'passengers_return',
    'pickup_place',
    'pickup_place_detail',
    'disembark_detail',
    'disembark_time',
    'train_arrival_time',
    'pickup_time',
]);

export function ContactForm({ activeSuccessModal, activeErrorModal, t }) {
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState(initialState);
    const [phoneError, setPhoneError] = useState(false);
    const [quotationDetails, setQuotationDetails] = useState(null);
    const [isQuoting, setIsQuoting] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const formRef = useRef();
    const bookingIdempotencyKeyRef = useRef(null);

    const updateFormData = (fields) => {
        bookingIdempotencyKeyRef.current = null;
        const shouldInvalidateQuote = Object.keys(fields).some((field) => quoteInputFields.has(field));

        if (shouldInvalidateQuote) {
            setQuotationDetails(null);
        }

        setFormData((prev) =>
            clearBlockedTimes({
                ...prev,
                ...fields,
                ...(shouldInvalidateQuote ? { total_quotation: 0 } : {}),
            })
        );
    };

    const allSteps = [1, 2, 3, 4, 5];
    const isBusy = isSending || isQuoting;

    const nextStep = async () => {
        const validationResult = validateStep(currentStep, formData);

        if (validationResult.isValid) {
            if (currentStep === 3) {
                setIsQuoting(true);
                try {
                    const calculatedQuotation = await requestBookingQuote(formData);
                    setQuotationDetails(calculatedQuotation);
                    setFormData((prev) => ({
                        ...prev,
                        total_quotation: calculatedQuotation.total_quotation,
                    }));
                    setCurrentStep(4);
                } catch (error) {
                    console.error('Failed to calculate booking quote:', error);
                    alert(error.message || t('stepper.validation.transfer'));
                } finally {
                    setIsQuoting(false);
                }
            } else {
                const currentStepIndex = allSteps.indexOf(currentStep);
                const nextStepValue = allSteps[currentStepIndex + 1];
                setCurrentStep(nextStepValue);
            }
        } else {
            setPhoneError(validationResult.isPhoneError || false);
            alert(t(validationResult.messageKey));
        }
    };

    const prevStep = () => {
        const currentStepIndex = allSteps.indexOf(currentStep);
        if (currentStepIndex > 0) {
            setCurrentStep(allSteps[currentStepIndex - 1]);
        }
    };

    const handleConfirmBooking = async () => {
        const validationResult = validateStep(currentStep, formData);

        if (!validationResult.isValid) {
            setPhoneError(validationResult.isPhoneError || false);
            alert(t(validationResult.messageKey));
            return;
        }

        setIsSending(true);
        try {
            bookingIdempotencyKeyRef.current ||= createBookingIdempotencyKey();
            const bookingResult = await createBooking(formData, bookingIdempotencyKeyRef.current);
            console.log('Booking created successfully:', bookingResult.bookingCode);
            activeSuccessModal.current.click();
            bookingIdempotencyKeyRef.current = null;
            setFormData(initialState);
            setQuotationDetails(null);
            setCurrentStep(1);
        } catch (error) {
            console.error('Failed to create booking:', error);
            activeErrorModal.current.click();
        } finally {
            setIsSending(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return <Step1Dates formData={formData} updateFormData={updateFormData} t={t} />;
            case 2:
                return <Step2Vehicles formData={formData} updateFormData={updateFormData} t={t} />;
            case 3:
                return <Step3Transfer formData={formData} updateFormData={updateFormData} t={t} />;
            case 4:
                return <Step4Quotation formData={formData} quotationDetails={quotationDetails} t={t} />;
            case 5:
                return (
                    <Step5Personal
                        formData={formData}
                        updateFormData={updateFormData}
                        quotationDetails={quotationDetails}
                        phoneError={phoneError}
                        t={t}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <div className="col-lg-7 col-xl-8 stepper-container">
                <form ref={formRef} className="booking-form d-flex flex-column">
                    <div className="stepper-progress">
                        {allSteps.map((stepNumber) => (
                            <div
                                key={stepNumber}
                                className={`step-indicator ${
                                    currentStep === stepNumber ? 'active' : currentStep >= stepNumber ? 'done' : ''
                                }`}
                            ></div>
                        ))}
                    </div>
                    {!isBusy ? (
                        <div className="flex-grow-1">{renderStep()}</div>
                    ) : (
                        <div className="text-center">
                            <div
                                className="spinner-border text-primary"
                                role="status"
                                style={{ width: '3rem', height: '3rem' }}
                            >
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 mb-0">{t('contact.loading')}</p>
                        </div>
                    )}
                    <div className="d-flex justify-content-between mt-4">
                        {currentStep > 1 && (
                            <button
                                type="button"
                                className="btn btn-outline-default"
                                onClick={prevStep}
                                disabled={isBusy}
                            >
                                {t('stepper.previous')}
                            </button>
                        )}
                        {currentStep < 3 && (
                            <button
                                type="button"
                                className="btn btn-primary ms-auto"
                                onClick={nextStep}
                                disabled={isBusy}
                            >
                                {t('stepper.next')}
                            </button>
                        )}
                        {currentStep === 3 && (
                            <button
                                type="button"
                                className="btn btn-primary ms-auto"
                                onClick={nextStep}
                                disabled={isBusy}
                            >
                                {t('stepper.getQuote')}
                            </button>
                        )}
                        {currentStep === 4 && (
                            <button
                                type="button"
                                className="btn btn-success ms-auto"
                                onClick={nextStep}
                                disabled={isBusy}
                            >
                                {t('quoteModal.acceptButton')}
                            </button>
                        )}
                        {currentStep === 5 && (
                            <button
                                type="button"
                                className="btn btn-success ms-auto"
                                onClick={handleConfirmBooking}
                                disabled={isBusy}
                            >
                                {t('stepper.confirmBooking')}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </>
    );
}
